import { GridApi } from 'ag-grid-community';
import { INPUT_SIZES } from 'cdk-radial';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { ROLES, EVENT_NAME, Tabs } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { SELECT } from 'components/CashInBankAccountsDropdown/constants';
import { PaymentDatePicker } from 'components/PaymentDatePicker/PaymentDatePicker';
import { getPaymentDateByAuthorizePayment } from 'components/PaymentDatePicker/slice';
import { getCompanyId } from 'components/StoreSelect/slice';
import { useFetchOpenGlCalendars, useSubmitAuthorizedPayment } from 'hooks';
import { useUpdateInvoiceDescriptions } from 'hooks/useClientDataServices';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import {
  getUserRole,
  getUserRoleIsApOfficeManager
} from 'pages/LandingPage/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { formatCurrency } from 'utils/formatCurrency';
import { CashInBankAccountDropdown } from '../../components/CashInBankAccountsDropdown/CashInBankAccountDropdown';
import { formatLocaleDate } from '../../utils/dateUtils';
import { InvoiceTable } from './components';
import { AuthorizePaymentHeader } from './components/AuthorizePaymentHeader/AuthorizePaymentHeader';
import { useCIBAccountsOptions } from './helpers';
import { messages } from './messages';
import { getAuthorizeTotal, getGridApi } from './selector';
import {
  getUpdateInvoiceDescriptionsPayload,
  setAuthorizePaymentData
} from './slice';
import {
  StyledCIBPostingDesc,
  StyledCustomDatePickerDiv,
  StyledDatePicker,
  StyledDatePickerDiv,
  StyledDiv,
  StyledPostingDesc,
  StyledSpan,
  StyledTotal,
  StyledTotalAmount
} from './styled';
import { getFormattedList } from './utils';

import type {
  DropdownOption,
  InputSizes
} from '../../components/CashInBankAccountsDropdown/types';
import type {
  AuthorizationPaymentRecordType,
  CIBDropdownResult
} from './types';

export const AuthorizePaymentJPMC = (): React.JSX.Element => {
  const amplitude = useAmplitude();
  const intl: IntlShape = useIntl();
  const {
    cashInBankAccountsOptions,
    isLoading: isLoadingCBA
  }: CIBDropdownResult = useCIBAccountsOptions();
  const { data: openGlCalendarsData } = useFetchOpenGlCalendars();
  const dispatch = useAppDispatch();
  const gridApi: GridApi | null = useAppSelector(getGridApi);
  const authorizePaymentDate = useAppSelector(getPaymentDateByAuthorizePayment);
  const totalAmount = useAppSelector(getAuthorizeTotal);
  const companyId = useAppSelector(getCompanyId);
  const userRoleIsApOfficeManager = useAppSelector(
    getUserRoleIsApOfficeManager
  );
  const updateInvoiceDescriptionsPayload = useAppSelector(
    getUpdateInvoiceDescriptionsPayload
  );
  const selectedTab = useAppSelector(getSelectedTab);

  const {
    mutate: updateInvoiceDescriptions,
    isLoading: isUpdateInvoiceDescriptionsLoading
  } = useUpdateInvoiceDescriptions();

  const { mutate: authorizeReviewedData, isLoading } =
    useSubmitAuthorizedPayment();

  const userRole = useAppSelector(getUserRole);
  const [postingDescription, setPostingDesc] = useState<string>('');
  const [cashInBankAccount, setCashInBankAccount] = useState<string>(SELECT);
  const [cashInBankAccountLabel, setCashInBankAccountLabel] =
    useState<string>('');
  const [postingDescriptionFlag, setPostingDescriptionFlag] = useState<
    boolean | undefined
  >(true);
  const [isInValidCashInBankAccount, setIsInValidCashInBankAccount] =
    useState<boolean>(false);
  const [hasPaymentDateError, setPaymentDateError] = useState<boolean>(false);
  const [hasPostingDescError, setPostingDescError] = useState<
    boolean | undefined
  >(true);

  const validateDesc = (desc: string) => {
    const isInValid = desc === '' && postingDescriptionFlag;
    setPostingDescError(isInValid);
    return !isInValid;
  };

  const validateCashInBankAccount = (cashInBankAcc: string) => {
    const isInValid = cashInBankAcc === SELECT;
    setIsInValidCashInBankAccount(isInValid);
    return !isInValid;
  };

  const handlePostingDesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPostingDesc(event.target?.value);
    validateDesc(event.target?.value);
  };

  useEffect(() => {
    if (!postingDescriptionFlag) {
      setPostingDescError(false);
    } else if (postingDescriptionFlag && postingDescription === '') {
      setPostingDescError(true);
    }
  }, [postingDescriptionFlag, postingDescription]);

  const handleAuthorizePayment = () => {
    const authorizePaymentList: AuthorizationPaymentRecordType[] = [];
    gridApi?.forEachNode(node => authorizePaymentList.push(node.data));
    const validPostingDesc = validateDesc(postingDescription);
    const validCBA = validateCashInBankAccount(cashInBankAccount);

    if (amplitude && selectedTab === Tabs.ToBePaid) {
      amplitudeTracker(amplitude, EVENT_NAME.RequestAuthorization).catch(err =>
        console.log('error in tracking Request Authorization event', err)
      );
    }

    if (validPostingDesc && validCBA && !hasPaymentDateError) {
      amplitudeTracker(amplitude, EVENT_NAME.AuthorizePayment, {
        'Vendor Total': authorizePaymentList.length
      });
      const authorizePaymentReview = {
        paymentDate: authorizePaymentDate,
        companyId,
        cashInBankAccount,
        postingDescription,
        authorizationPaymentList: getFormattedList(authorizePaymentList)
      };
      dispatch(setAuthorizePaymentData(authorizePaymentReview));
      if (updateInvoiceDescriptionsPayload.length > 0) {
        updateInvoiceDescriptions(updateInvoiceDescriptionsPayload);
      }
      authorizeReviewedData(authorizePaymentReview);
    }
  };

  const { SMALL } = INPUT_SIZES as InputSizes;

  const onCashInBankAccountChange = useCallback(
    (cashInBankAcc: DropdownOption) => {
      setCashInBankAccount(cashInBankAcc.value);
      setCashInBankAccountLabel(cashInBankAcc.label);
      setPostingDescriptionFlag(cashInBankAcc.postingDescriptionRequired);
    },
    []
  );

  const datePicker = () => (
    <StyledDatePicker>
      <PaymentDatePicker
        glCalendarData={openGlCalendarsData}
        setError={setPaymentDateError}
      />
    </StyledDatePicker>
  );

  return (
    <>
      <AuthorizePaymentHeader
        handleAuthorizePayment={handleAuthorizePayment}
        disableAuthButton={userRole === ROLES.AP_CLERK}
        isLoading={isLoading || isUpdateInvoiceDescriptionsLoading}
      />
      <StyledDiv>
        <StyledTotalAmount>
          <StyledTotal>{intl.formatMessage(messages.total)}</StyledTotal>
          <StyledSpan>{formatCurrency(totalAmount)}</StyledSpan>
        </StyledTotalAmount>
        <StyledCIBPostingDesc>
          <CashInBankAccountDropdown
            cashInBankAccounts={cashInBankAccountsOptions || []}
            onCashInBankAccountChange={onCashInBankAccountChange}
            isLoading={isLoadingCBA}
            isValidCashInBankAccount={isInValidCashInBankAccount}
          />
          <StyledPostingDesc
            data-testid="postingDescription"
            isRequired={postingDescriptionFlag}
            label={intl.formatMessage(messages.postingDescription)}
            name="input-error"
            onChange={handlePostingDesChange}
            value={postingDescription}
            errorMessage={intl.formatMessage(
              messages.postingDescriptionErrorMessage
            )}
            hasError={hasPostingDescError}
            enableCustomValidation
            onError={(errors: string) => {
              setPostingDescError(errors.length > 0);
            }}
            disabled={userRole === ROLES.AP_CLERK}
            size={SMALL}
          />
        </StyledCIBPostingDesc>
      </StyledDiv>
      {userRoleIsApOfficeManager ? (
        <StyledCustomDatePickerDiv>{datePicker()}</StyledCustomDatePickerDiv>
      ) : (
        <StyledDatePickerDiv>{datePicker()}</StyledDatePickerDiv>
      )}
      <InvoiceTable
        paymentDate={formatLocaleDate(authorizePaymentDate)}
        postingDescription={postingDescription}
        selectedCIBAccount={cashInBankAccountLabel}
      />
    </>
  );
};
